import { useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { Card, FormControl, LoadingButton, PageTitle, PageWrapper } from '@ingrid/luterkort-react';

import { FormLeaveBlocker } from '~src/components/FormLeaveBlocker/FormLeaveBlocker';
import { MutationBanner } from '~src/components/MutationNotification/MutationNotification';
import { MutationSaveBar } from '~src/components/MutationSaveBar/MutationSaveBar';
import { useTranslation } from '~src/i18n/use-translation';
import { useToast } from '~src/main/hooks/useToast';
import { hasTouchedFields } from '~src/utils/form';

import { Breadcrumb } from '../breadcrumb';
import { BasicInfo } from '../create/basic-info';
import { Credentials } from '../create/credentials';
import { Menu } from '../menu';
import { useCarrierContractUpdateMutation } from '../mutations';
import { useCarrierContractSuspenseQuery } from '../queries';
import { CarrierContractUpsert, useCarrierContractForm } from '../types';
import { Carrier } from './carrier';

export function Page() {
  const { id } = useParams();
  const { data } = useCarrierContractSuspenseQuery({ contract_id: id });

  const form = useCarrierContractForm({
    defaultValues: {
      carrier_configuration_data: data.carrier_configuration_data,
      description: data.description ?? '',
      schema_id: data.schema_id,
      name: data.name,
    },
  });

  const mutation = useCarrierContractUpdateMutation({ contract_id: id });
  const viewportRef = useRef(null);

  const navigate = useNavigate();

  const disableClear = !form.formState.isDirty || !hasTouchedFields(form.formState.touchedFields);

  const { t } = useTranslation();
  const { toast } = useToast();

  async function onSubmit(data: CarrierContractUpsert) {
    await mutation.mutateAsync({
      update_mask: Object.keys(form.formState.dirtyFields),
      ...data,
    });

    toast({
      title: t('carrierContracts.details.updateSuccess'),
      variant: 'success',
    });

    navigate(`../${id}`);
  }

  function handleClear() {
    form.reset(data, { keepDefaultValues: true, keepValues: false, keepTouched: false });
  }

  return (
    <PageWrapper variant="center" style={{ marginBottom: MutationSaveBar.Offset }}>
      <FormProvider {...form}>
        <FormControl.Root>
          <FormControl.Form onSubmit={form.handleSubmit(onSubmit)} style={{ display: 'none' }} />

          <PageTitle.Wrapper ref={viewportRef}>
            <PageTitle.Stack>
              <Breadcrumb currentPage={t('common.edit')} />
            </PageTitle.Stack>

            <FormControl.SubmitButton asChild>
              <LoadingButton loading={mutation.isPending}>{t('common.save')}</LoadingButton>
            </FormControl.SubmitButton>

            <Menu disabled={disableClear}>
              <Menu.ClearItem onClick={handleClear} disabled={disableClear} />
              <Menu.DuplicateItem contractId={data.contract_id!} />
              <Menu.DeleteItem contractId={data.contract_id!} onSuccess={() => navigate('..')} />
            </Menu>
          </PageTitle.Wrapper>

          <MutationBanner mutation={mutation} />

          <Card.List>
            <BasicInfo />
            <Carrier contract={data} />
            <Credentials />
          </Card.List>

          <FormLeaveBlocker formState={form.formState} />

          <MutationSaveBar viewportRef={viewportRef}>
            <MutationSaveBar.Cancel to=".." />

            <MutationSaveBar.Submit
              isLoading={mutation.isPending}
              onClick={form.handleSubmit(onSubmit)}
              label={t('common.save')}
            />
          </MutationSaveBar>
        </FormControl.Root>
      </FormProvider>
    </PageWrapper>
  );
}
